/**
 * Hard redirect to `/login`, with the return-target
 * handled correctly for BOTH ways a session can end.
 *
 * ONE has two full-page logout redirects, and they must treat the
 * user's current location oppositely:
 *
 *   - **Involuntary** (session expiry — the WS `4002 AUTH_FAILED` kick
 *     in `lib/socket/socket.ts`): the user did not choose to leave, so
 *     the current location is remembered via `rememberReturnTo` before
 *     the redirect. A full-page `window.location.href` navigation never
 *     mounts App.tsx's unauthenticated catch-all, so the
 *     router-level capture (`<RedirectToLogin>`) cannot see the page
 *     being abandoned — the capture has to happen here, at the redirect
 *     moment.
 *
 *   - **Deliberate** (ProfileDrawer sign-out → auth-identity `logout()`
 *     → main.tsx `onLogout`): the user chose to leave, so nothing is
 *     preserved — and any stale target (e.g. an unconsumed push
 *     deep-link) is cleared so the NEXT login lands on home, not on a
 *     page from the previous session.
 *
 * These two callsites are the discriminator: `onLogout` only ever fires
 * from the deliberate `logout()` action (auth-identity calls it nowhere
 * else), while the expiry kick redirects directly from the socket close
 * handler without touching `logout()`. No flag threading needed.
 *
 * The open-redirect guard stays in `lib/returnTo.ts` — `rememberReturnTo`
 * rejects anything that is not a same-app path (e.g. a `//host` pathname),
 * and the read side re-checks, so this module adds no new trust surface.
 */

import { clearReturnTo, rememberReturnTo } from './returnTo';

/**
 * Best-effort full-page navigation to `/login`. No-op when already
 * there (avoids a reload loop) or when navigation is unavailable
 * (jsdom, sandboxed iframes).
 */
function hardRedirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  try {
    window.location.href = '/login';
  } catch {
    // jsdom or a sandboxed env may refuse navigation; ignore.
  }
}

/**
 * Involuntary session end (WS 4002 / expiry kick): remember where the
 * user was (path + search + hash) so the post-login redirect
 * (`PostLoginRedirect` / `AuthCallback`) can put them back, then bounce
 * to `/login`. Unsafe locations are silently not stored (returnTo's
 * guard); the redirect still happens.
 */
export function redirectToLoginPreservingLocation(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') {
    rememberReturnTo(
      window.location.pathname + window.location.search + window.location.hash,
    );
  }
  hardRedirectToLogin();
}

/**
 * Deliberate sign-out (main.tsx `onLogout`): never resurrect a stale
 * target — clear it, then bounce to `/login`. The clear runs even when
 * already on `/login`, so a stored target cannot leak into the next
 * session.
 */
export function redirectToLoginForSignOut(): void {
  clearReturnTo();
  hardRedirectToLogin();
}
